import type { Affirmation, AffirmationSet, SyncResponse, Working } from "@mantra/shared";
import { api, isOnline } from "./api";
import { db, kvGet, kvSet, type Profile } from "./db";

const BATCH = 200;
let running: Promise<void> | null = null;
let again = false;
let timer: ReturnType<typeof setTimeout> | undefined;

const strip = <T extends { dirty: 0 | 1 }>({ dirty: _d, ...rest }: T) => rest;

/** Применяем серверную запись, если локально нет более свежей неотправленной правки */
async function applyServer<T extends { id: string; updatedAt: string }>(
  table: typeof db.sets | typeof db.affirmations | typeof db.workings,
  rows: T[],
) {
  if (!rows.length) return;
  const t = table as unknown as import("dexie").Table<T & { dirty: 0 | 1 }, string>;
  const locals = await t.bulkGet(rows.map((r) => r.id));
  const put: (T & { dirty: 0 | 1 })[] = [];
  rows.forEach((r, i) => {
    const l = locals[i];
    if (l && l.dirty && l.updatedAt > r.updatedAt) return;
    put.push({ ...r, dirty: 0 });
  });
  await t.bulkPut(put);
}

/** Снимаем флаг dirty только с тех записей, которые не менялись, пока шёл запрос */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function markClean(
  table: import("dexie").Table<any, string>,
  sent: { id: string; updatedAt: string }[],
) {
  const sentAt = new Map(sent.map((s) => [s.id, s.updatedAt]));
  await table
    .where("id")
    .anyOf([...sentAt.keys()])
    .modify((row: { id: string; updatedAt: string; dirty: 0 | 1 }) => {
      if (row.updatedAt === sentAt.get(row.id)) row.dirty = 0;
    });
}

async function pushProfile() {
  if (!(await kvGet<boolean>("profileDirty"))) return;
  const p = await kvGet<Profile>("profile");
  if (!p) return;
  await api("/v1/me", { method: "PATCH", json: { gender: p.gender, settings: { defaultPreset: p.defaultPreset } } });
  await kvSet("profileDirty", false);
}

async function pushPractice() {
  const dirty = await db.practice.where("dirty").equals(1).limit(100).toArray();
  if (!dirty.length) return;
  await api("/v1/practice", { method: "POST", json: dirty.map(strip) });
  await db.practice.bulkPut(dirty.map((p) => ({ ...p, dirty: 0 as const })));
}

async function runOnce() {
  let hasMore = true;
  while (hasMore) {
    const cursor = (await kvGet<string>("syncCursor")) ?? "0";
    const sets = await db.sets.where("dirty").equals(1).limit(BATCH).toArray();
    const affs = await db.affirmations.where("dirty").equals(1).limit(BATCH * 5).toArray();
    const works = await db.workings.where("dirty").equals(1).limit(50).toArray();
    const res = await api<SyncResponse>("/v1/sync", {
      method: "POST",
      json: { cursor, changes: { sets: sets.map(strip), affirmations: affs.map(strip), workings: works.map(strip) } },
    });
    await db.transaction("rw", [db.sets, db.affirmations, db.workings, db.kv], async () => {
      await markClean(db.sets, sets);
      await markClean(db.affirmations, affs);
      await markClean(db.workings, works);
      await applyServer<AffirmationSet>(db.sets, res.sets);
      await applyServer<Affirmation>(db.affirmations, res.affirmations);
      await applyServer<Working>(db.workings, res.workings ?? []);
      if (res.rejected.length) {
        // чужие id — отбрасываем локальную копию, чтобы не слать её вечно
        await db.sets.bulkDelete(res.rejected);
        await db.affirmations.bulkDelete(res.rejected);
        await db.workings.bulkDelete(res.rejected);
      }
      await kvSet("syncCursor", res.cursor);
    });
    const stillDirty =
      (await db.sets.where("dirty").equals(1).count()) +
      (await db.affirmations.where("dirty").equals(1).count()) +
      (await db.workings.where("dirty").equals(1).count());
    hasMore = res.hasMore || (stillDirty > 0 && (sets.length === BATCH || affs.length === BATCH * 5 || works.length === 50));
  }
  await pushPractice();
  await pushProfile();
  await kvSet("lastSyncAt", Date.now());
}

export function syncNow(): Promise<void> {
  if (!isOnline()) return Promise.resolve();
  if (running) {
    again = true;
    return running;
  }
  running = runOnce()
    .catch((e) => console.warn("sync failed", e))
    .finally(() => {
      running = null;
      if (again) {
        again = false;
        void syncNow();
      }
    });
  return running;
}

/** Дебаунс после локальных правок: пачка изменений уходит одним запросом */
export function scheduleSync(delay = 1500) {
  clearTimeout(timer);
  timer = setTimeout(() => void syncNow(), delay);
}

export function startBackgroundSync() {
  void syncNow();
  window.addEventListener("online", () => void syncNow());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void syncNow();
  });
  setInterval(() => {
    if (document.visibilityState === "visible") void syncNow();
  }, 60_000);
}
